import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { syncReminders } from "./reminders";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import { BackupMenu } from "./BackupMenu";
import { PhotoViewer } from "./PhotoViewer";
import { readDownscaledPhoto } from "./photo";
import {
  store, todayLocal, daysAgoLabel, fmtShortDate, waterRhythmCopy, kindLabel, kindEmoji,
  CARE_KINDS, type Plant, type CareEvent, type CareKind, type WeekSignals, type PlantSignals,
  type PlantWeekRow, type HeadlineFact, type FactTone, type SpeciesId, type GrowthEntry,
} from "./store";
import {
  narrateThisWeek, narratePlantStory, askAboutPlant, identifySpecies, crossesHumanSafetyFence,
  careProfile, type CareProfile, diagnose, type Diagnosis,
  openWebLogin, BrainError, REFUSAL_MESSAGE, type BrainErrorCode,
} from "./brain";
import { OrioraBadge } from "./OrioraBadge";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, X, Lock, Leaf, WifiOff, RefreshCw, Droplet, ChevronLeft, Plus,
  ShieldAlert, Coins, Check, Copy, MessageCircleQuestion, CalendarClock, Sprout, Camera, TrendingUp, ArrowLeft } from "lucide-react";

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out. (mirrors expense_audit_ai)
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground. THIS is the reliable catch for
  // "deleted my account, came back, still signed in": returning to the app (closing the
  // in-app delete window, or re-opening from the app switcher) re-checks the token, the
  // server rejects the deleted account, and we drop to login.
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-plant-diary://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-plant-diary://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      // Clear the on-device session WITHOUT a server /logout (the account is gone → global
      // signOut errors). Then FORCE the logged-out UI directly — onAuthStateChange/SIGNED_OUT
      // is unreliable with the async Tauri file storage, so don't depend on it.
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (SCREENSHOT) return <Home email="you@oriora.app" onSignedOut={() => {}} />;
  if (!consented) {
    return <AiConsent brand="Oriora Plant Diary"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <BootScreen />;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

// Calm branded boot screen — never a bare "Loading…".
// Sim/screenshot rig: 1 = bypass consent+login so shot runs can reach every screen.
// ⚠️ MUST be 0 in any shipped build (per-app ledger checks this).
const SCREENSHOT = 0;

// ── "THE SILL" (2026-08-06 redesign) — clay on greenhouse green; window-light rake ──
// Glyphs render ONLY via CSS masks (.glyphm/.markm) — the SVGs are currentColor and
// go black inside an <img> on dark themes (the Study bug, caught at extraction).
import glyphWater from "./assets/glyph-water.svg";
import glyphFeed from "./assets/glyph-feed.svg";
import glyphRepot from "./assets/glyph-repot.svg";
import glyphPrune from "./assets/glyph-prune.svg";
import glyphNote from "./assets/glyph-note.svg";
import glyphMilestone from "./assets/glyph-milestone.svg";
import glyphPhoto from "./assets/glyph-photo.svg";
import glyphGrowth from "./assets/glyph-growth.svg";
import glyphMist from "./assets/glyph-mist.svg";
import markPlaceholder from "./assets/mark-plant-placeholder.svg";
import momentEmptyCollection from "./assets/moment-empty-collection.svg";
import momentFirstWater from "./assets/moment-first-water.svg";
const GLYPH_URL: Record<string, string> = {
  water: `url("${glyphWater}")`, feed: `url("${glyphFeed}")`, repot: `url("${glyphRepot}")`,
  prune: `url("${glyphPrune}")`, note: `url("${glyphNote}")`, milestone: `url("${glyphMilestone}")`,
  photo: `url("${glyphPhoto}")`, growth: `url("${glyphGrowth}")`, mist: `url("${glyphMist}")`,
};
function Glyphm({ kind, size = 18, className = "" }: { kind: string; size?: number; className?: string }) {
  return <i className={`glyphm ${className}`} style={{ ["--m" as never]: GLYPH_URL[kind] ?? GLYPH_URL.note, width: size, height: size }} aria-hidden="true" />;
}
function Markm({ src, size = 104 }: { src: string; size?: number }) {
  return <i className="markm" style={{ ["--m" as never]: `url("${src}")`, width: size, height: size }} aria-hidden="true" />;
}
const WEEKDAY = () => new Date().toLocaleDateString(undefined, { weekday: "long" });
const DATE_LINE = () => new Date().toLocaleDateString(undefined, { day: "numeric", month: "long" });

function BootScreen() {

  return (
    <main style={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
      <div className="empty-glow" style={{ textAlign: "center", padding: "2rem" }}>
        <div className="empty-art pulse-soft"><Leaf size={36} style={{ color: "var(--accent)" }} /></div>
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "1rem" }}>Loading your diary…</div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))", background: "linear-gradient(180deg, var(--bg-grad-top) 0%, var(--bg) 60%)" }}>
      <div className="card fade-up empty-glow" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 2rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
        <div className="empty-art" style={{ marginBottom: "1rem" }}><Leaf size={36} style={{ color: "var(--accent)" }} /></div>
        <div className="eyebrow" style={{ justifyContent: "center" }}><Sprout size={13} /> Oriora Plant Diary</div>
        <h1 className="serif" style={{ fontSize: "1.6rem", margin: "0.55rem 0 0.5rem" }}>A quiet diary for your plants</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Log each watering, feeding, and repot. Oriora reads back each plant's own rhythm — your care
          log is stored on this device unless you back it up.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 48 }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-plant-diary");
              else if (p === "macos") await signInMac("oriora-plant-diary");
              else if (p === "web") window.location.href = "/login?next=/oriora-plant-diary/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
        <p className="muted" style={{ fontSize: "var(--fs-xxs)", lineHeight: 1.5, marginTop: "1rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. AI narration runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
      </div>
    </main>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openPlant, setOpenPlant] = useState<Plant | null>(null);
  const [showAddPlant, setShowAddPlant] = useState(false);
  const [editPlant, setEditPlant] = useState<Plant | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Plant | null>(null);
  const [logFor, setLogFor] = useState<Plant | null>(null);
  // THE POUR — write-on-tap watering ritual (R2). pouring drives the card animation;
  // drawerFor keeps the 6s after-drawer with Undo + kind switch.
  const [pouringId, setPouringId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ plantId: string; eventId: string; kind: CareKind; firstEver: boolean; days: number | null } | null>(null);
  const drawerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — it does not
  // change any existing screen. onChanged → reload() refreshes the collection after a chat mutation.
  const [chatOpen, setChatOpen] = useState(false);

  // Cross-plant week signals, recomputed on every data change (on-device, instant).
  const [week, setWeek] = useState<WeekSignals | null>(null);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const recapRef = useRef<HTMLDivElement>(null);

  // Body scrolls: the plant page and view swaps must land at the top (P6c).
  useEffect(() => { try { document.body.scrollTo(0, 0); window.scrollTo(0, 0); } catch { /* ignore */ } }, [openPlant?.id]);

  const reload = useCallback(async () => {
    const ps = await store.listPlants();
    setPlants(ps);
    setWeek(await store.computeWeekSignals(todayLocal()));
    setLoaded(true);
    // Keep the open plant in sync if it still exists.
    setOpenPlant((cur) => (cur ? ps.find((p) => p.id === cur.id) ?? null : null));
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // On-device reminders: fire a local notification at 9am on the day each plant next comes due
  // by ITS OWN watering rhythm (last-water + the plant's average interval). Purely factual —
  // describes the user's own logged cadence, never advises. Native only; best-effort. Overdue
  // plants are skipped here (the in-app "This week" board already surfaces them).
  useEffect(() => {
    if (platform() === "web" || !week) return;
    const reminders = week.plants
      .filter((p) => p.avg_water_interval_days != null && p.days_since_water != null)
      .map((p) => {
        const dueInDays = Math.round((p.avg_water_interval_days as number) - (p.days_since_water as number));
        if (dueInDays < 1) return null;
        const at = new Date();
        at.setHours(9, 0, 0, 0);
        at.setDate(at.getDate() + dueInDays);
        return { key: p.plant_id, title: "Time to water", body: `${p.name} is due for watering`, at };
      })
      .filter((r): r is { key: string; title: string; body: string; at: Date } => r !== null);
    void syncReminders(reminders);
  }, [week]);

  // Native-menu actions (Actions menu in the macOS menu bar): Add plant / Log care / This week.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add_plant": setShowAddPlant(true); break;
        case "log_care": setLogFor(plants[0] ?? null); break;
        case "this_week":
          recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          recapRef.current?.querySelector<HTMLButtonElement>("button")?.click();
          break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, [plants]);

  const doExport = useCallback(async () => {
    if (plants.length === 0) return;
    // Gather every plant's events for a single chronological CSV.
    const all: CareEvent[] = [];
    for (const p of plants) all.push(...(await store.listEvents(p.id)));
    if (all.length === 0) return;
    const { csv, filename } = buildExport(plants, all);
    const path = await saveFileDialog({ defaultPath: filename });
    if (path) await writeTextFile(path, csv);
  }, [plants]);

  async function doDeletePlant(id: string) {
    setConfirmDelete(null);
    await store.deletePlant(id);
    setOpenPlant((cur) => (cur?.id === id ? null : cur));
    await reload();
  }

  // ── Plant detail view ──────────────────────────────────────────────────────

  const weekRowByPlant = new Map((week?.plants ?? []).map((r) => [r.plant_id, r]));

  // ── ROUNDS (ledger row 2b): a plant is a living thing AT A PLACE with its own watering rhythm,
  // and people water by walking the house room to room — so the collection is grouped by room and
  // ordered by what is past its rhythm, not alphabetically. The app already computed
  // `water_over_under_days` (it drives the 9am reminders) and the flat A–Z list ignored it: the
  // screen answered "what plants exist" when the question is "what needs me, and where".
  // over_under_days > 0 = past its own usual gap. Never a rule about how often a plant SHOULD be
  // watered — only a description of the rhythm this person has actually kept.
  async function pour(plant: Plant, row?: PlantWeekRow) {
    try {
      const firstEver = (week?.events_this_week ?? 0) >= 0 && !row?.days_since_water && row?.days_since_water !== 0;
      const days = row?.days_since_water ?? null;
      const ev = await store.createEvent({ plant_id: plant.id, kind: "water" });
      setPouringId(plant.id);
      setTimeout(() => setPouringId((c) => (c === plant.id ? null : c)), 1000);
      if (drawerTimer.current) clearTimeout(drawerTimer.current);
      setDrawer({ plantId: plant.id, eventId: ev.id, kind: "water", firstEver, days });
      drawerTimer.current = setTimeout(() => setDrawer(null), 6000);
      await reload();
    } catch (e) { console.error("pour failed", e); }
  }
  async function undoPour() {
    if (!drawer) return;
    if (drawerTimer.current) clearTimeout(drawerTimer.current);
    try { await store.deleteEvent(drawer.eventId); } catch { /* keep UI honest anyway */ }
    setDrawer(null);
    await reload();
  }
  async function switchPourKind(kind: CareKind) {
    if (!drawer || kind === drawer.kind) return;
    try {
      await store.deleteEvent(drawer.eventId);
      const ev = await store.createEvent({ plant_id: drawer.plantId, kind });
      if (drawerTimer.current) clearTimeout(drawerTimer.current);
      setDrawer({ ...drawer, eventId: ev.id, kind });
      drawerTimer.current = setTimeout(() => setDrawer(null), 6000);
      await reload();
    } catch (e) { console.error("kind switch failed", e); }
  }

  const rounds = useMemo(() => {
    const over = (id: string) => weekRowByPlant.get(id)?.water_over_under_days ?? null;
    const rooms = new Map<string, Plant[]>();
    for (const pl of plants) {
      const room = (pl.location || "").trim() || "Unplaced";
      (rooms.get(room) ?? rooms.set(room, []).get(room)!).push(pl);
    }
    const groups = [...rooms.entries()].map(([room, list]) => {
      const sorted = [...list].sort((a, b) => {
        const av = over(a.id), bv = over(b.id);
        // Plants with no watering logged yet sort last: there is nothing to be past.
        if (av === null && bv === null) return a.name.localeCompare(b.name);
        if (av === null) return 1;
        if (bv === null) return -1;
        return bv - av;
      });
      const worst = sorted.reduce<number | null>((acc, pl) => {
        const v = over(pl.id);
        return v === null ? acc : acc === null ? v : Math.max(acc, v);
      }, null);
      return { room, list: sorted, worst, dueCount: sorted.filter((pl) => (over(pl.id) ?? -1) > 0).length };
    });
    // The room with the most-overdue plant leads. "Unplaced" never jumps the queue on a tie.
    groups.sort((a, b) => {
      if (a.worst === null && b.worst === null) return a.room.localeCompare(b.room);
      if (a.worst === null) return 1;
      if (b.worst === null) return -1;
      if (b.worst !== a.worst) return b.worst - a.worst;
      return a.room.localeCompare(b.room);
    });
    return groups;
  }, [plants, week]);

  if (openPlant) {
    return (
      <>
        <PlantDetail
          plant={openPlant}
          offline={offline}
          onBack={() => setOpenPlant(null)}
          onChanged={reload}
          onLogCare={() => setLogFor(openPlant)}
          onEdit={() => setEditPlant(openPlant)}
          onDelete={() => setConfirmDelete(openPlant)}
        />
        {logFor && <LogCare plant={logFor} onClose={() => setLogFor(null)} onSaved={() => { setLogFor(null); void reload(); }} />}
        {editPlant && <PlantForm editing={editPlant} onClose={() => setEditPlant(null)} onSaved={() => { setEditPlant(null); void reload(); }} />}
        {confirmDelete && (
          <ConfirmDeletePlant plant={confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={() => doDeletePlant(confirmDelete.id)} />
        )}
      </>
    );
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(1.75rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <div className="sill-top">
        <div style={{ minWidth: 0 }}>
          <h1 className="sill-title serif">{WEEKDAY()}</h1>
          <div className="sill-sub">
            {DATE_LINE()}
            {plants.length > 0 && <> · {plants.length} plant{plants.length === 1 ? "" : "s"}{(week?.plants_past_water_rhythm ?? 0) > 0 ? `, ${week!.plants_past_water_rhythm} past its usual gap` : ""}</>}
          </div>
        </div>
        <div className="sill-tools row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <button className="sill-tool" aria-label="Type it instead — add, log, find, recap" title="Type it instead"
            style={{ color: "var(--accent)" }} onClick={() => setChatOpen(true)}>
            <Glyphm kind="note" size={17} />
          </button>
          <BackupMenu
            appName="Plant Diary" fileSlug="plant-diary" appScheme="oriora-plant-diary"
            hasData={plants.length > 0} onRestored={reload}
            onExportCsv={plants.length > 0 ? () => void doExport() : undefined}
          />
          <AccountMenu email={email} appSlug="oriora-plant-diary" appName="Oriora Plant Diary" onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </div>

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={15} style={{ flexShrink: 0, marginTop: 1, color: "var(--warning)" }} />
          <div>Narration pauses while you're offline — your log still saves on this device.</div>
        </div>
      )}

      {!loaded ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.6rem" }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 68 }} />)}
        </div>
      ) : plants.length === 0 ? (
        <>
          {/* READY-STATE (2026-09-19): the promise is "log each watering; the rhythm builds
              itself", and with no plants the rhythm — the object of that promise — was invisible:
              one art card, a sentence, a pill. The card is now DRAWN with its rhythm parts in
              place (the state line and the gap band on the photo's bottom edge), so a new owner
              sees what a watering builds before they log one. */}
          <EmptyBench onAdd={() => setShowAddPlant(true)} onSaved={reload} />
          {!chatOpen && (
            <button className="opc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
              <Glyphm kind="note" size={16} /> Type it instead <span className="hint muted">"add plant Monstera"</span>
            </button>
          )}
        </>
      ) : (
        <div className="rounds">
          {rounds.map((g) => (
            <section key={g.room} className="round-room">
              <div className="round-head">
                <span className="round-name">{g.room}</span>
                <span className="round-note muted">
                  {g.dueCount > 0
                    ? `${g.dueCount} past its usual gap`
                    : g.worst === null ? `${g.list.length} plant${g.list.length === 1 ? "" : "s"}` : "on rhythm"}
                </span>
              </div>
              <div className="round-grid">
                {g.list.map((p) => (
                  <PlantTile key={p.id} plant={p} row={weekRowByPlant.get(p.id)}
                    pouring={pouringId === p.id}
                    drawer={drawer?.plantId === p.id ? drawer : null}
                    onOpen={() => setOpenPlant(p)}
                    onPour={() => void pour(p, weekRowByPlant.get(p.id))}
                    onUndo={() => void undoPour()}
                    onKind={(k) => void switchPourKind(k)}
                    onMore={() => { setDrawer(null); setLogFor(p); }} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {plants.length > 0 && (
        <section className="week fade-up">
          <div>
            <div className="label">The week</div>
            <div className="week-fig">
              <span className="week-num tnum serif">{week?.events_this_week ?? 0}</span>
              <span className="week-cap">care event{(week?.events_this_week ?? 0) === 1 ? "" : "s"} across {plants.length} plant{plants.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div ref={recapRef}>
            <WeekNarration week={week} offline={offline} />
          </div>
        </section>
      )}

      {plants.length > 0 && (
        <>
          <button className="fab" onClick={() => setShowAddPlant(true)} aria-label="Add plant">
            <Plus size={17} /> Add a plant
          </button>
          {!chatOpen && (
            <button className="opc-launch" onClick={() => setChatOpen(true)} aria-label="Open chat assistant">
              <Glyphm kind="note" size={16} /> Type it instead <span className="hint muted">add · log · find · recap</span>
            </button>
          )}
        </>
      )}

      {showAddPlant && <PlantForm editing={null} onClose={() => setShowAddPlant(false)} onSaved={() => { setShowAddPlant(false); void reload(); }} />}
      {logFor && <LogCare plant={logFor} onClose={() => setLogFor(null)} onSaved={() => { setLogFor(null); void reload(); }} />}
      {editPlant && <PlantForm editing={editPlant} onClose={() => setEditPlant(null)} onSaved={() => { setEditPlant(null); void reload(); }} />}
      {confirmDelete && (
        <ConfirmDeletePlant plant={confirmDelete} onCancel={() => setConfirmDelete(null)} onConfirm={() => doDeletePlant(confirmDelete.id)} />
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          + slide-over live on top; no existing screen is altered. A chat-driven change reloads the
          collection via onChanged → reload. */}
      <aside className={`opc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="opc-head">
          <span className="label">Type it — add, log, find, recap</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

// ── THE BENCH, DRAWN — what a plant card becomes once it is watered twice ─────────
// The parts are the real ones: the placeholder mark in `.plant-photo-empty`, the veil and scrim,
// the `.plant-state` line, the `.gap-band` on the bottom edge and the pour button. Nothing here
// is interactive — it is the shape of the thing, shown before there is one.
function EmptyBench({ onAdd, onSaved }: { onAdd: () => void; onSaved: () => Promise<void> | void }) {
  return (
    <>
      <div className="pd-ghost fade-up" aria-hidden>
        <article className="plant-card">
          <div className="plant-photo-empty"><Markm src={markPlaceholder} size={104} /></div>
          <div className="plant-veil" />
          <div className="plant-scrim" />
          <div className="plant-body">
            <div className="pd-ghost-rule" style={{ width: "58%", height: 13 }} />
            <div className="plant-state tnum none" style={{ marginTop: 10 }}>Day — of its usual —</div>
          </div>
          <span className="pour-btn pd-ghost-pour" aria-hidden><Glyphm kind="water" size={25} /></span>
          <div className="gap-band"><span className="gap-fill" style={{ ["--gap" as string]: "0%" }} /></div>
        </article>
      </div>

      <div className="pd-ghost-cap">
        Tap the drop each time you water. The <b>second</b> watering is the one that matters — the
        days between them become this plant's usual gap, and the band along the bottom of its photo
        fills as the next one comes due. Measure its height or count its leaves whenever you like
        and a <b>growth line</b> draws itself on the plant's own page.
      </div>

      <FirstPlant onSaved={onSaved} onFullForm={onAdd} />

      <div className="pd-note">
        <span className="pd-note-lbl">Where this lives</span>
        The plants, the photos and the log are written to this device. A copy goes to your
        <b> own</b> GitHub or Drive when you back it up. There is no subscription — only the AI
        moments cost anything, and only when you ask for one.
      </div>
    </>
  );
}

// ── THE DOOR — a plant and its first watering, in one line ───────────────────────
// The full plant form asks for name, species (with a photo identify step), room, potted-on date
// and a photo. This asks for the name, whether it was watered today, and optionally the room —
// and writes the plant plus, if ticked, its first `water` event, which is where the rhythm starts.
function FirstPlant({ onSaved, onFullForm }: { onSaved: () => Promise<void> | void; onFullForm: () => void }) {
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");
  const [wateredToday, setWateredToday] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ready = name.trim() !== "";

  async function save() {
    if (!ready || saving) return;
    setSaving(true); setErr(null);
    try {
      const p = await store.createPlant({ name: name.trim(), location: room.trim() });
      if (wateredToday) await store.createEvent({ plant_id: p.id, kind: "water", done_on: todayLocal() });
      setName(""); setRoom("");
      await onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="pd-first" aria-label="Add the first plant">
      <div className="pd-first-lbl">Put one on the bench</div>
      <input className="pd-first-name" placeholder="What do you call it?" value={name}
        onChange={(e) => { setName(e.target.value); setErr(null); }}
        onKeyDown={(e) => { if (e.key === "Enter") void save(); }} aria-label="Plant name" />
      <input className="pd-first-room" placeholder="Which room? (optional)" value={room}
        onChange={(e) => setRoom(e.target.value)} aria-label="Room (optional)" />
      <button type="button" className={`pd-first-check${wateredToday ? " on" : ""}`}
        aria-pressed={wateredToday} onClick={() => setWateredToday((v) => !v)}>
        <span className="pd-first-box">{wateredToday ? <Check size={14} /> : null}</span>
        Watered it today — start the rhythm here
      </button>
      <div className="pd-first-hint">
        No interval to set: the gap between your loggings is the interval, and it is this plant's,
        not a species default.
      </div>
      {err && <div className="pd-first-err">{err}</div>}
      <button className="btn btn-primary pd-first-save" disabled={!ready || saving} onClick={save}>
        {saving ? "Saving…" : "Put it on the bench"}
      </button>
      <button type="button" className="pd-first-full" onClick={onFullForm}>
        or open the full card — photo, species from a photo, potted-on date
      </button>
    </section>
  );
}

function CollectionHero({ week }: { week: WeekSignals | null }) {
  const count = week?.plant_count ?? 0;
  const overdue = week?.plants_past_water_rhythm ?? 0;
  const evts = week?.events_this_week ?? 0;
  return (
    <div className="hero fade-up">
      <div className="row" style={{ gap: "1rem", flexWrap: "nowrap" }}>
        <span className="hero-icon"><Leaf size={28} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="serif" style={{ fontSize: "1.95rem", fontWeight: 600, lineHeight: 1.05 }}>
            {count === 0 ? "No plants yet" : `${count} ${count === 1 ? "plant" : "plants"}`}
          </div>
          <div style={{ fontSize: "var(--fs-sm)", opacity: 0.9, marginTop: 2 }}>
            {count === 0
              ? "Add a plant to start its care timeline"
              : overdue > 0
                ? `${overdue} past ${overdue === 1 ? "its" : "their"} usual watering rhythm`
                : "All on their usual rhythm"}
          </div>
          {count > 0 && (
            <div className="row tnum" style={{ fontSize: "var(--fs-xs)", opacity: 0.7, marginTop: 6, gap: "0.3rem" }}>
              <CalendarClock size={12} /> {evts} care {evts === 1 ? "event" : "events"} this week
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlantTile({ plant, row, pouring, drawer, onOpen, onPour, onUndo, onKind, onMore }: {
  plant: Plant; row?: PlantWeekRow; pouring: boolean;
  drawer: { kind: CareKind; firstEver: boolean; days: number | null } | null;
  onOpen: () => void; onPour: () => void; onUndo: () => void; onKind: (k: CareKind) => void; onMore: () => void;
}) {
  const since = row?.days_since_water ?? null;
  const past = row?.past_water_rhythm ?? false;
  const avg = row?.avg_water_interval_days ?? null;
  const gapPct = since != null && avg ? Math.min(100, Math.round((since / avg) * 100)) : 0;
  const overPct = past && since != null && avg ? Math.min(40, Math.round(((since - avg) / avg) * 100)) : 0;
  const stateCopy = drawer ? "Watered just now"
    : since == null ? "No watering logged yet"
    : past ? `Day ${since} — usual gap is ${Math.round(avg ?? 0)}`
    : avg ? `Day ${since} of its usual ${Math.round(avg)}`
    : `Watered ${since === 0 ? "today" : `${since}d ago`}`;
  return (
    <div>
      <article className={`plant-card${pouring ? " pouring" : ""}`} role="button" tabIndex={0} onClick={onOpen}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}>
        {plant.photo_ref ? (
          <div className="plant-photo" style={{ backgroundImage: `url(${plant.photo_ref})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ) : (
          <div className="plant-photo-empty"><Markm src={markPlaceholder} size={104} /></div>
        )}
        <div className="plant-veil" />
        {/* The scrim is what makes the cream name legible, so it belongs on EVERY card — not only
            the ones with a photo. Gated on photo_ref, a plant added without a photo rendered
            #FFF8EC text straight onto the pale empty-slot gradient: the name was there, and
            unreadable. Found by adding a plant and looking at the card. */}
        <div className="plant-scrim" />
        <div className="plant-body">
          <div className="plant-name serif">{plant.name}</div>
          {plant.species && <div className="plant-species">{plant.species}</div>}
          <div className={`plant-state tnum${drawer ? "" : past ? " past" : since == null ? " none" : ""}`}>{stateCopy}</div>
        </div>
        {pouring && <div className="pour-wipe" aria-hidden="true" />}
        <button className={`pour-btn${drawer ? " done" : ""}`} aria-label={`Log a watering for ${plant.name}`}
          onClick={(ev) => { ev.stopPropagation(); if (!drawer) onPour(); }}>
          {drawer ? <Check size={22} /> : <Glyphm kind="water" size={25} />}
        </button>
        <div className={`gap-band${past && !drawer ? " past" : ""}`}
          style={{ ["--gap" as never]: `${drawer ? 0 : gapPct}%`, ["--over" as never]: `${overPct}%` }}>
          <i className="gap-fill" />
        </div>
      </article>
      {drawer && (
        <div className="after-drawer" onClick={(e) => e.stopPropagation()}>
          {drawer.firstEver ? (
            <div className="drawer-msg" style={{ alignItems: "center", gap: "0.7rem" }}>
              <Markm src={momentFirstWater} size={52} />
              <span><b>{plant.name}, day one</b> — one watering logged. After the next one, {plant.name} will have a gap of its own.</span>
              <button className="btn-link" onClick={onUndo}>Undo</button>
            </div>
          ) : (
            <div className="drawer-msg">
              <span><b>{CARE_KINDS.find((k) => k.kind === drawer.kind)?.label ?? "Logged"}</b> · today{drawer.days != null ? ` · ${drawer.days} day${drawer.days === 1 ? "" : "s"} after the last` : ""}</span>
              <button className="btn-link" onClick={onUndo}>Undo</button>
            </div>
          )}
          <div className="drawer-row">
            {(["water", "feed", "repot", "prune"] as CareKind[]).map((k) => (
              <button key={k} className={`kind-rung${drawer.kind === k ? " selected" : ""}`} onClick={() => onKind(k)}>
                <Glyphm kind={k} size={17} /><span className="lbl">{CARE_KINDS.find((c) => c.kind === k)?.verb}</span>
              </button>
            ))}
            <button className="kind-rung" onClick={onMore}><Glyphm kind="note" size={17} /><span className="lbl">More…</span></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── The ONE collection-level AI feature: "This week" narration ──────────────────
type NarrErr = { code: BrainErrorCode | "empty"; message: string };

function WeekNarration({ week, offline }: { week: WeekSignals | null; offline: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<NarrErr | null>(null);

  const hasData = (week?.plant_count ?? 0) > 0 && (week?.plants ?? []).some((p) => p.days_since_water !== null);

  async function run() {
    if (!week) return;
    setBusy(true); setErr(null); setText(null); setFresh(false);
    try {
      const r = await narrateThisWeek(week);
      const t = r.text.trim();
      if (!t) setErr({ code: "empty", message: "Log some care first — there's nothing to narrate yet." });
      else { setText(t); setFresh(true); setTimeout(() => setFresh(false), 700); }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Narration failed — please try again." });
    } finally { setBusy(false); }
  }

  return (
    <div className={`recap${fresh ? " fresh" : ""} fade-up`} style={{ padding: "1.1rem 1.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="eyebrow" style={{ flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ flexShrink: 0 }} /> This week
        </div>
        <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }} disabled={busy || !hasData || offline} onClick={run}>
          {busy ? "Narrating…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Narrate my week"}
        </button>
      </div>

      {!busy && !text && !err && hasData && !offline && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
          A plain-words read of each plant's watering rhythm — who's on their usual gap, who's past it. Your care history, never advice.
        </div>
      )}
      {!busy && !text && !hasData && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8 }}>Log a watering or two to enable a weekly narration.</div>
      )}
      {!busy && !text && !err && hasData && offline && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8 }}>Reconnect to narrate your week. Your log is safe on this device.</div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading your collection's rhythm…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
          <div style={{ marginTop: 6, paddingTop: 8, borderTop: "1px solid var(--border-subtle)", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
            <div className="skeleton-line" style={{ width: "82%", height: 9 }} />
            <div className="skeleton-line" style={{ width: "74%", height: 9 }} />
          </div>
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={run} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <FactList facts={week?.headline_facts ?? []} />
          <div className="row" style={{ marginTop: 12, justifyContent: "space-between", gap: "0.6rem" }}>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)", flex: 1, minWidth: 0 }}>Reads back your own log. Anything beyond it is summarised with sources, or flagged as AI reasoning — never health or safety advice.</span>
            <CopyButton text={copyableNarration(text, week?.headline_facts ?? [])} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Plant detail: cadence + Story + Ask + timeline ──────────────────────────────
function PlantDetail({ plant, offline, onBack, onChanged, onLogCare, onEdit, onDelete }:
  { plant: Plant; offline: boolean; onBack: () => void; onChanged: () => void; onLogCare: () => void; onEdit: () => void; onDelete: () => void }) {
  const [events, setEvents] = useState<CareEvent[]>([]);
  const [growth, setGrowth] = useState<GrowthEntry[]>([]);
  const [signals, setSignals] = useState<PlantSignals | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [confirmEvent, setConfirmEvent] = useState<CareEvent | null>(null);
  const [addingGrowth, setAddingGrowth] = useState(false);
  const wc = signals?.cadence.find((c) => c.kind === "water") ?? null;

  const reload = useCallback(async () => {
    setEvents(await store.listEvents(plant.id));
    setGrowth(await store.listGrowth(plant.id));
    setSignals(await store.computePlantSignals(plant.id, todayLocal()));
    setLoaded(true);
    // Keyed on the plant OBJECT, not just its id: saves made from sheets owned by Home (Log care,
    // Edit) end in Home.reload(), which hands this screen a fresh plant object. That is the signal
    // to re-read events + signals, otherwise the cadence cards and Story/Ask stay stale.
  }, [plant]);
  useEffect(() => { void reload(); }, [reload]);

  async function doDeleteEvent(id: string) {
    setConfirmEvent(null);
    await store.deleteEvent(id);
    await reload();
    onChanged();
  }

  async function doDeleteGrowth(id: string) {
    await store.deleteGrowth(id);
    await reload();
  }

  // Species carries a small provenance tag when it was filled from a photo identification.
  const speciesLabel = plant.species
    ? plant.species_confidence
      ? `${plant.species} (photo-ID · ${plant.species_confidence} confidence)`
      : plant.species
    : "";
  const subtitle = [speciesLabel, plant.location].filter(Boolean).join(" · ");

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      padding: "max(1.75rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(6.5rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
<div className="bio-hero">
        {plant.photo_ref ? (
          <div className="plant-photo" style={{ position: "absolute", inset: 0, backgroundImage: `url(${plant.photo_ref})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        ) : (
          <div className="plant-photo-empty" style={{ position: "absolute", inset: 0 }}><Markm src={markPlaceholder} size={132} /></div>
        )}
        <div className="plant-veil" />
        <div className="plant-scrim" />
        <button className="bio-back" aria-label="Back to the rooms" onClick={onBack}><ArrowLeft size={18} /></button>
        <div className="row" style={{ position: "absolute", top: 14, right: 14, gap: "0.3rem", zIndex: 3 }}>
          <button className="btn btn-sm btn-pill btn-on-hero" onClick={onEdit}>Edit</button>
          <button className="btn btn-sm btn-pill btn-on-hero" title="Delete plant" aria-label="Delete plant" onClick={onDelete}><Trash2 size={14} /></button>
        </div>
        <div className="bio-head">
          <h1 className="bio-title serif">{plant.name}</h1>
          <div className="bio-sub">
            {[plant.species, plant.location, plant.potted_on ? `potted ${plant.potted_on}` : ""].filter(Boolean).join(" · ")}
          </div>
        </div>
        {wc?.avg_interval_days != null && wc?.days_since_last != null && (
          <div className={`gap-band${wc.days_since_last > wc.avg_interval_days ? " past" : ""}`}
            style={{ ["--gap" as never]: `${Math.min(100, Math.round((wc.days_since_last / wc.avg_interval_days) * 100))}%` }}>
            <i className="gap-fill" />
          </div>
        )}
      </div>

      {/* Deterministic cadence cards (computed in Rust; NO AI) */}
      {loaded ? <CadenceGrid signals={signals} /> : (
        <div className="stat-grid">{[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 96 }} />)}</div>
      )}

      {wc?.avg_interval_days != null && wc?.days_since_last != null && (
        <p className="cadence-line">
          {plant.name}'s usual gap is <em>{Math.round(wc.avg_interval_days)} day{Math.round(wc.avg_interval_days) === 1 ? "" : "s"}</em>. It has been <em>{wc.days_since_last}</em>.
        </p>
      )}
      <button className="btn btn-primary btn-pill" style={{ marginTop: "0.75rem", minHeight: 44, width: "100%" }} onClick={onLogCare}>
        <Plus size={16} /> Log care
      </button>

      {/* Owner-set health state (#11) — a plain chip, not advice. */}
      {loaded && <HealthPicker plant={plant} onChanged={() => { void reload(); onChanged(); }} />}

      {/* The AI care plan — reasoned per species, tuned to THIS plant's rhythm. The moment Planta
          charges for, here as an AI call and better because it adapts. */}
      {loaded && <CarePlan plant={plant} signals={signals} wc={wc} offline={offline} onSaved={() => { void reload(); onChanged(); }} />}

      {/* Sick-plant diagnosis — photo + THIS plant's history. The thing people actually pay for. */}
      {loaded && <DiagnosePanel plant={plant} signals={signals} offline={offline} />}

      {/* The per-plant AI Story + Ask */}
      <PlantStory plant={plant} signals={signals} offline={offline} />

      <div className="sec-head">
        <h2><CalendarClock size={16} style={{ color: "var(--accent)" }} /> Care timeline</h2>
        <span className="chip" style={{ gap: "0.25rem" }}><Lock size={11} /> on this device</span>
      </div>

      {/* The diary, not just the schedule: what it looked like, and how it's coming along. */}
      {loaded && <Album plant={plant} events={events} onAdded={() => { void reload(); onChanged(); }} />}
      {loaded && <GrowthPanel entries={growth} onAdd={() => setAddingGrowth(true)} onDelete={(id) => void doDeleteGrowth(id)} />}

      {!loaded ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>{[0, 1].map((i) => <div key={i} className="skeleton-block" style={{ height: 56 }} />)}</div>
      ) : events.length === 0 ? (
        <div className="card empty-glow" style={{ padding: "2.25rem 1.5rem", textAlign: "center", borderRadius: "var(--radius-lg)" }}>
          <div className="empty-art" style={{ width: 60, height: 60, fontSize: "1.9rem" }}>💧</div>
          <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.75rem", lineHeight: 1.5 }}>No care logged yet. Tap “Log care” after you water, feed, or repot.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }}>
          {events.map((ev) => (
            <EventTile key={ev.id} ev={ev} onDelete={() => setConfirmEvent(ev)} />
          ))}
        </div>
      )}

      {addingGrowth && (
        <LogGrowth plant={plant} onClose={() => setAddingGrowth(false)}
          onSaved={() => { setAddingGrowth(false); void reload(); }} />
      )}

      {confirmEvent && (
        <CenterSheet title="Delete this entry?" onCancel={() => setConfirmEvent(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.1rem", lineHeight: 1.55 }}>
            Delete the {kindLabel(confirmEvent.kind).toLowerCase()} on {fmtShortDate(confirmEvent.done_on)}? This can’t be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn" onClick={() => setConfirmEvent(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => doDeleteEvent(confirmEvent.id)}>Delete</button>
          </div>
        </CenterSheet>
      )}
    </main>
  );
}

// Owner-set health chip (#11). Plain state the owner taps — never derived, never advice. Tapping
// the current state again clears it. On-device only; rides the plant's own backup.
const HEALTH_STATES: { key: string; label: string }[] = [
  { key: "thriving", label: "Thriving" },
  { key: "ok", label: "OK" },
  { key: "struggling", label: "Struggling" },
];
function HealthPicker({ plant, onChanged }: { plant: Plant; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  async function set(next: string) {
    if (busy) return;
    setBusy(true);
    try { await store.updatePlant(plant.id, { health: plant.health === next ? "" : next }); onChanged(); }
    finally { setBusy(false); }
  }
  return (
    <div className="row" style={{ gap: "0.4rem", alignItems: "center", marginTop: "0.75rem", flexWrap: "wrap" }}>
      <span className="muted" style={{ fontSize: "var(--fs-sm)", marginRight: "0.15rem" }}>How's it doing?</span>
      {HEALTH_STATES.map((h) => {
        const on = plant.health === h.key;
        return (
          <button key={h.key} className="btn btn-sm btn-pill" disabled={busy} onClick={() => set(h.key)}
            style={on ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : undefined}
            aria-pressed={on}>
            {h.label}
          </button>
        );
      })}
    </div>
  );
}

// The AI care plan for one plant. The MODEL is the knowledge (no species table); the brain reasons
// a profile for the species, tunes it to this plant's OWN watering rhythm, and (server-side) can
// fold in the owner's own rules. We cache the answer on the plant so re-opening is free — a fresh
// plan is only fetched when the owner asks (Generate / Refresh). "Next water" prefers the plant's
// OWN observed rhythm over the species default the moment there's enough history — the differentiator.
function CarePlan(
  { plant, signals, wc, offline, onSaved }:
  { plant: Plant; signals: PlantSignals | null; wc: { avg_interval_days: number | null; days_since_last: number | null } | null; offline: boolean; onSaved: () => void },
) {
  const cached: CareProfile | null = (() => {
    if (!plant.care_profile) return null;
    try { return JSON.parse(plant.care_profile) as CareProfile; } catch { return null; }
  })();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function generate() {
    if (busy || offline) return;
    setBusy(true); setErr(null);
    try {
      const profile = await careProfile(plant.species, plant.location, signals, "");
      await store.updatePlant(plant.id, { care_profile: JSON.stringify(profile) });
      onSaved();
    } catch (e) {
      setErr(e instanceof BrainError ? e.message : "Couldn't build the care plan — try again.");
    } finally { setBusy(false); }
  }

  // Next water: the plant's OWN observed interval wins once it exists; the AI plan is the default
  // for a plant too new to have a rhythm. Pure date maths — free, instant, no call.
  const interval = wc?.avg_interval_days ?? cached?.water_days ?? null;
  const since = wc?.days_since_last ?? null;
  let dueLine: string | null = null;
  if (interval != null && since != null) {
    const inDays = Math.round(interval - since);
    dueLine = inDays <= 0
      ? (inDays === 0 ? "Water due today" : `Water ${-inDays} day${-inDays === 1 ? "" : "s"} overdue`)
      : `Next water in about ${inDays} day${inDays === 1 ? "" : "s"}`;
  } else if (cached?.water_days != null) {
    dueLine = `Suggested watering: about every ${cached.water_days} days`;
  }
  const usingOwnRhythm = wc?.avg_interval_days != null;

  if (!plant.species && !cached) {
    return (
      <div className="card" style={{ padding: "1rem 1.1rem", marginTop: "0.75rem", borderRadius: "var(--radius-lg)" }}>
        <div className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>
          <Sparkles size={14} style={{ color: "var(--accent)", verticalAlign: "-2px" }} /> Add the species (or identify it from a photo) to get an AI care plan for this plant.
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: "1rem 1.1rem", marginTop: "0.75rem", borderRadius: "var(--radius-lg)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: cached ? "0.6rem" : 0 }}>
        <h2 style={{ margin: 0, fontSize: "var(--fs-md)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Sparkles size={15} style={{ color: "var(--accent)" }} /> Care plan
        </h2>
        {cached && (
          <button className="btn btn-sm btn-pill" disabled={busy || offline} onClick={generate} title="Rebuild with the latest history">
            <RefreshCw size={13} /> {busy ? "…" : "Refresh"}
          </button>
        )}
      </div>

      {!cached ? (
        <>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.85rem", lineHeight: 1.5 }}>
            Get an AI care plan for your {plant.species || "plant"} — watering, light, what to watch for — tuned to how you actually care for this one.
          </p>
          <button className="btn btn-primary btn-pill" style={{ minHeight: 42, width: "100%" }} disabled={busy || offline} onClick={generate}>
            {busy ? "Building your plan…" : offline ? "Offline — reconnect to build" : "Build care plan"}
          </button>
        </>
      ) : (
        <>
          {dueLine && (
            <div className="cadence-line" style={{ marginTop: 0 }}>
              <strong>{dueLine}.</strong>{" "}
              <span className="muted">{usingOwnRhythm ? "based on this plant's own rhythm" : "AI suggestion until its own rhythm builds"}</span>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.5rem", margin: "0.75rem 0" }}>
            {cached.light && <CareFact label="Light" value={cached.light} />}
            {cached.humidity && <CareFact label="Humidity" value={cached.humidity} />}
            {cached.soil && <CareFact label="Soil" value={cached.soil} />}
            {cached.feed && <CareFact label="Feeding" value={cached.feed} />}
          </div>
          {cached.watch_for.length > 0 && (
            <div style={{ marginTop: "0.4rem" }}>
              <div className="muted" style={{ fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.35rem" }}>Watch for</div>
              <ul style={{ margin: 0, paddingLeft: "1.1rem", fontSize: "var(--fs-sm)", lineHeight: 1.55 }}>
                {cached.watch_for.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {cached.note && <p className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.75rem", lineHeight: 1.5, fontStyle: "italic" }}>{cached.note}</p>}
        </>
      )}
      {err && <p style={{ color: "var(--danger, #c0544a)", fontSize: "var(--fs-sm)", marginTop: "0.6rem" }}>{err}</p>}
    </div>
  );
}

// Sick-plant diagnosis. Photo of the struggling plant → the brain reasons over the image AND this
// plant's own care history (the edge). Results are transient (a moment, not a stored profile) — the
// owner acts on them; the photo stays on-device except for the single billed vision call.
function DiagnosePanel(
  { plant, signals, offline }: { plant: Plant; signals: PlantSignals | null; offline: boolean },
) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Diagnosis | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function pick() { if (!busy && !offline) fileRef.current?.click(); }

  function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setErr(null); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); setBusy(false); return; }
      const img = new Image();
      img.onload = () => { void run(downscale(img, src)); };
      img.onerror = () => { void run(src); };
      img.src = src;
    };
    reader.onerror = () => { setErr("Could not read that photo — try another."); setBusy(false); };
    reader.readAsDataURL(file);
  }
  function downscale(img: HTMLImageElement, fallback: string): string {
    try {
      const MAX = 1024;
      const scale = Math.min(1, MAX / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement("canvas"); c.width = w; c.height = h;
      const ctx = c.getContext("2d"); if (!ctx) return fallback;
      ctx.drawImage(img, 0, 0, w, h);
      return c.toDataURL("image/jpeg", 0.82);
    } catch { return fallback; }
  }
  async function run(dataUrl: string) {
    try { setResult(await diagnose(plant.species, dataUrl, signals)); }
    catch (e) { setErr(e instanceof BrainError ? e.message : "Couldn't diagnose that — try again."); }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "1rem 1.1rem", marginTop: "0.75rem", borderRadius: "var(--radius-lg)" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: result ? "0.6rem" : 0 }}>
        <h2 style={{ margin: 0, fontSize: "var(--fs-md)", display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <ShieldAlert size={15} style={{ color: "var(--accent)" }} /> Something looks wrong?
        </h2>
        {result && (
          <button className="btn btn-sm btn-pill" disabled={busy || offline} onClick={pick} title="Diagnose from a new photo">
            <Camera size={13} /> New photo
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={onPicked} style={{ display: "none" }} />

      {!result ? (
        <>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.85rem", lineHeight: 1.5 }}>
            Photograph the problem — yellowing, spots, pests, wilting. The AI reads the photo <em>and</em> this plant's own care history to suggest what's going on.
          </p>
          <button className="btn btn-primary btn-pill" style={{ minHeight: 42, width: "100%" }} disabled={busy || offline} onClick={pick}>
            <Camera size={16} /> {busy ? "Looking at your plant…" : offline ? "Offline — reconnect to diagnose" : "Diagnose from a photo"}
          </button>
        </>
      ) : (
        <>
          {result.summary && <p style={{ fontSize: "var(--fs-sm)", margin: "0 0 0.75rem", lineHeight: 1.55 }}>{result.summary}</p>}
          {result.likely_causes.length > 0 ? (
            <div style={{ display: "grid", gap: "0.6rem" }}>
              {result.likely_causes.map((c, i) => (
                <div key={i} style={{ background: "var(--bg-muted, rgba(0,0,0,0.03))", borderRadius: "var(--radius-md, 10px)", padding: "0.65rem 0.8rem" }}>
                  <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>{c.cause}</div>
                  {c.why && <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: "0.2rem", lineHeight: 1.5 }}>{c.why}</div>}
                  {c.check && <div style={{ fontSize: "var(--fs-sm)", marginTop: "0.35rem", lineHeight: 1.5 }}><strong>Try:</strong> {c.check}</div>}
                </div>
              ))}
            </div>
          ) : (
            <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.5 }}>Couldn't tell from that photo — try a clearer, closer shot of the affected part.</p>
          )}
          {result.note && <p className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: "0.75rem", lineHeight: 1.5, fontStyle: "italic" }}>{result.note}</p>}
        </>
      )}
      {err && <p style={{ color: "var(--danger, #c0544a)", fontSize: "var(--fs-sm)", marginTop: "0.6rem" }}>{err}</p>}
    </div>
  );
}

function CareFact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "var(--bg-muted, rgba(0,0,0,0.03))", borderRadius: "var(--radius-md, 10px)", padding: "0.55rem 0.7rem" }}>
      <div className="muted" style={{ fontSize: "var(--fs-xs)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: "var(--fs-sm)", marginTop: "0.15rem", lineHeight: 1.4 }}>{value}</div>
    </div>
  );
}

function CadenceGrid({ signals }: { signals: PlantSignals | null }) {
  const cad = signals?.cadence ?? [];
  const meta: Record<string, { label: string; emoji: string }> = {
    water: { label: "Watering", emoji: "💧" },
    feed: { label: "Feeding", emoji: "🧪" },
    repot: { label: "Repotting", emoji: "🪴" },
  };
  return (
    <div className="stat-grid">
      {cad.map((c) => {
        const m = meta[c.kind] ?? { label: c.kind, emoji: "•" };
        const since = c.days_since_last;
        return (
          <div key={c.kind} className={`stat${c.past_personal_rhythm ? " watch" : ""}`}>
            <div className="stat-lbl"><span>{m.emoji}</span> {m.label}</div>
            <div className="stat-val tnum">
              {since === null ? "—" : since === 0 ? "Today" : (<>{since}<span className="unit">d</span></>)}
            </div>
            <div className="stat-sub tnum">
              {since === null
                ? "not logged yet"
                : c.avg_interval_days === null
                  ? "first one logged"
                  : c.past_personal_rhythm
                    ? `past its ~${c.avg_interval_days}d rhythm`
                    : `usual ~${c.avg_interval_days}d`}
            </div>
            {(c.past_personal_rhythm || c.longest_gap_this_year || (c.trend && c.trend !== "steady")) && (
              <div className="row" style={{ gap: "0.3rem", marginTop: 8 }}>
                {c.past_personal_rhythm && <span className="chip warn" style={{ fontSize: "var(--fs-xxs)", padding: "0.12rem 0.45rem" }}>past rhythm</span>}
                {c.longest_gap_this_year && since !== null && <span className="chip" style={{ fontSize: "var(--fs-xxs)", padding: "0.12rem 0.45rem" }}>longest this year</span>}
                {/* Deterministic trend read (recent-3 gaps vs all-time avg; computed in Rust, observational). */}
                {c.trend === "tightening" && <span className="chip" style={{ fontSize: "var(--fs-xxs)", padding: "0.12rem 0.45rem" }}>tighter lately</span>}
                {c.trend === "widening" && <span className="chip" style={{ fontSize: "var(--fs-xxs)", padding: "0.12rem 0.45rem" }}>wider lately</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EventTile({ ev, onDelete }: { ev: CareEvent; onDelete: () => void }) {
  return (
    <div className="tile" style={{ cursor: "default" }}>
      {/* A moment with a photo shows the photo instead of its emoji — the album, inline. */}
      {ev.photo_ref ? (
        <img src={ev.photo_ref} alt="" style={{ width: 38, height: 38, objectFit: "cover", borderRadius: "var(--radius-md)", flexShrink: 0 }} />
      ) : (
        <span className="tile-icon" style={{ background: "var(--elev-2)" }}>{kindEmoji(ev.kind)}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>
          {kindLabel(ev.kind)} <span className="muted" style={{ fontWeight: 400 }}>· {fmtShortDate(ev.done_on)} · {daysAgoLabel(ev.done_on)}</span>
        </div>
        {ev.note && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, lineHeight: 1.45 }}>{ev.note}</div>}
      </div>
      <button className="tile-del" title="Delete entry" aria-label="Delete entry" onClick={onDelete}>
        <Trash2 size={15} />
      </button>
    </div>
  );
}

// ── The album ──────────────────────────────────────────────────────────────────
// Every photo this plant has collected, oldest → newest, so you can scrub through and watch it
// change. Purely the user's own photos; nothing is sent anywhere.
//
// "Add photo" lives HERE, not buried in Log care. Before this, the only way to attach a photo was
// the Log-care sheet — which defaults to "Watered" — so "here's my plant today" silently logged a
// watering that never happened. A photo-only entry is now its own kind, and the album shows from
// the very first photo (hiding it until two made the feature invisible to a new user).
function Album({ plant, events, onAdded }: { plant: Plant; events: CareEvent[]; onAdded: () => void }) {
  const shots = [...events].filter((e) => e.photo_ref).reverse(); // events arrive newest-first
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [viewIdx, setViewIdx] = useState<number | null>(null);

  async function onPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const photo_ref = await readDownscaledPhoto(file);
      await store.createEvent({ plant_id: plant.id, kind: "photo", done_on: todayLocal(), note: "", photo_ref });
      onAdded();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Could not add that photo — try another.");
    } finally { setBusy(false); }
  }

  return (
    <section style={{ marginBottom: "1.1rem" }}>
      <input ref={inputRef} type="file" accept="image/*" onChange={(e) => void onPicked(e)} style={{ display: "none" }} />
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div className="eyebrow" style={{ margin: 0 }}>
          <Camera size={13} /> Album{shots.length ? ` · ${shots.length} photo${shots.length === 1 ? "" : "s"}` : ""}
        </div>
        <button type="button" className="btn btn-sm btn-pill" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? "Adding…" : "Add photo"}
        </button>
      </div>

      {shots.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          Snap {plant.name} today, then again next week — you’ll have its whole story to scroll back through.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
          {shots.map((ev, idx) => (
            <figure key={ev.id} style={{ margin: 0, flexShrink: 0, width: 92 }}>
              <img src={ev.photo_ref} alt={`${kindLabel(ev.kind)} on ${fmtShortDate(ev.done_on)}`}
                onClick={() => setViewIdx(idx)}
                style={{ width: 92, height: 92, objectFit: "cover", borderRadius: "var(--radius-md)", display: "block", cursor: "zoom-in" }} />
              <figcaption className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 3, textAlign: "center" }}>
                {fmtShortDate(ev.done_on)}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {viewIdx !== null && (
        <PhotoViewer
          photos={shots.map((ev) => ({ src: ev.photo_ref, alt: `${kindLabel(ev.kind)} on ${fmtShortDate(ev.done_on)}` }))}
          startIndex={viewIdx}
          onClose={() => setViewIdx(null)}
        />
      )}

      {err && <div className="notice notice-err" style={{ marginTop: "0.6rem" }}>{err}</div>}
    </section>
  );
}

// ── Growth ─────────────────────────────────────────────────────────────────────
// Height and/or leaf count over time, plus a bare sparkline. This is what makes the plant
// a project you can see progress on. Observational only — it plots what the owner measured.
function GrowthPanel({ entries, onAdd, onDelete }: { entries: GrowthEntry[]; onAdd: () => void; onDelete: (id: string) => void }) {
  const heights = entries.filter((e) => e.height_cm != null);
  const leaves = entries.filter((e) => e.leaf_count != null);

  // First → latest change, stated as a plain fact (never "good"/"bad" — that'd be a judgement).
  const delta = (() => {
    if (heights.length >= 2) {
      const d = (heights[heights.length - 1].height_cm as number) - (heights[0].height_cm as number);
      return `${d >= 0 ? "+" : ""}${round1(d)} cm since ${fmtShortDate(heights[0].measured_on)}`;
    }
    if (leaves.length >= 2) {
      const d = (leaves[leaves.length - 1].leaf_count as number) - (leaves[0].leaf_count as number);
      return `${d >= 0 ? "+" : ""}${d} leaves since ${fmtShortDate(leaves[0].measured_on)}`;
    }
    return null;
  })();

  return (
    <section style={{ marginBottom: "1.1rem" }}>
      <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
        <div className="eyebrow" style={{ margin: 0 }}><TrendingUp size={13} /> Growth</div>
        <button type="button" className="btn btn-sm btn-pill" onClick={onAdd}>Add measurement</button>
      </div>

      {entries.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>
          Measure its height or count its leaves now and then — you’ll see how it’s coming along.
        </div>
      ) : (
        <div className="card" style={{ padding: "0.85rem 1rem", borderRadius: "var(--radius-lg)" }}>
          {delta && <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: heights.length >= 2 ? "0.6rem" : 0 }}>{delta}</div>}
          {heights.length >= 2 && <Sparkline values={heights.map((e) => e.height_cm as number)} />}
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.35rem", marginTop: "0.7rem" }}>
            {[...entries].reverse().map((g) => (
              <div key={g.id} className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                <span className="muted" style={{ fontSize: "var(--fs-xs)", minWidth: 0 }}>
                  {fmtShortDate(g.measured_on)} ·{" "}
                  {[g.height_cm != null ? `${round1(g.height_cm)} cm` : null, g.leaf_count != null ? `${g.leaf_count} leaves` : null]
                    .filter(Boolean).join(" · ")}
                  {g.note ? ` — ${g.note}` : ""}
                </span>
                <button className="tile-del" title="Delete measurement" aria-label="Delete measurement" onClick={() => onDelete(g.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

const round1 = (n: number) => Math.round(n * 10) / 10;

/** A dependency-free sparkline. Flat series render as a flat line rather than dividing by zero. */
function Sparkline({ values }: { values: number[] }) {
  const W = 260, H = 40, PAD = 3;
  const min = Math.min(...values), max = Math.max(...values);
  const span = max - min || 1;
  const pts = values.map((v, i) => {
    const x = PAD + (i * (W - PAD * 2)) / Math.max(1, values.length - 1);
    const y = H - PAD - ((v - min) / span) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Height over time" style={{ display: "block" }}>
      <polyline points={pts.join(" ")} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function PlantStory({ plant, signals, offline }: { plant: Plant; signals: PlantSignals | null; offline: boolean }) {
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState(false);
  const [err, setErr] = useState<NarrErr | null>(null);
  const [question, setQuestion] = useState("");
  const [askText, setAskText] = useState<string | null>(null);
  const [askBusy, setAskBusy] = useState(false);
  const [askErr, setAskErr] = useState<NarrErr | null>(null);

  const hasData = (signals?.total_events ?? 0) > 0;
  // Live pre-check so the fence is visible before the user even sends (no billed call).
  const fenced = crossesHumanSafetyFence(question);

  const suggestions = ["How's it doing?", "What's my longest gap?", "Why might the leaves be yellowing?"];

  async function runStory() {
    if (!signals) return;
    setBusy(true); setErr(null); setText(null); setFresh(false);
    try {
      const r = await narratePlantStory(signals);
      const t = r.text.trim();
      if (!t) setErr({ code: "empty", message: "Log some care first — there's nothing to narrate yet." });
      else { setText(t); setFresh(true); setTimeout(() => setFresh(false), 700); }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Story failed — please try again." });
    } finally { setBusy(false); }
  }

  async function runAsk(q?: string) {
    if (!signals) return;
    const query = (q ?? question).trim();
    if (!query) return;
    if (q) setQuestion(q);
    if (crossesHumanSafetyFence(query)) { setAskErr({ code: "refused", message: REFUSAL_MESSAGE }); return; }
    setAskBusy(true); setAskErr(null); setAskText(null);
    try {
      const r = await askAboutPlant(query, signals);
      const t = r.text.trim();
      if (!t) setAskErr({ code: "empty", message: "No answer — try rephrasing your question." });
      else setAskText(t);
    } catch (e) {
      if (e instanceof BrainError) setAskErr({ code: e.code, message: e.message });
      else setAskErr({ code: "unavailable", message: "Question failed — please try again." });
    } finally { setAskBusy(false); }
  }

  return (
    <div className={`recap${fresh ? " fresh" : ""}`} style={{ padding: "1.1rem 1.25rem", marginTop: "1.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem" }}>
        <div className="eyebrow" style={{ flexWrap: "wrap" }}>
          <Sparkles size={14} style={{ flexShrink: 0 }} /> {plant.name}'s story
        </div>
        <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0 }} disabled={busy || !hasData || offline} onClick={runStory}>
          {busy ? "Writing…" : text ? (<><RefreshCw size={12} /> Refresh</>) : "Tell its story"}
        </button>
      </div>

      {!busy && !text && !err && hasData && !offline && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8, lineHeight: 1.55 }}>
          A few sentences on this plant's care rhythm — its usual gaps, the longest this year, what your last note said.
        </div>
      )}
      {!busy && !text && !hasData && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8 }}>Log some care to enable its story.</div>
      )}
      {!busy && !text && !err && hasData && offline && (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 8 }}>Reconnect to read its story. Your log is safe on this device.</div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.85rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> Reading {plant.name}'s rhythm…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "88%" }} />
          <div className="skeleton-line" style={{ width: "64%" }} />
        </div>
      )}

      {err && !busy && <BrainErr err={err} onRetry={runStory} />}

      {text && !busy && (
        <div className="fade-up" style={{ marginTop: "0.7rem" }} aria-live="polite">
          <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          <FactList facts={signals?.headline_facts ?? []} />
          <div className="row" style={{ marginTop: 12, justifyContent: "flex-end" }}>
            <CopyButton text={copyableNarration(text, signals?.headline_facts ?? [])} />
          </div>
        </div>
      )}

      {/* Ask — free-form question over THIS plant's history. PLANT fence applies. */}
      {hasData && (
        <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid var(--border-subtle)" }}>
          <div className="eyebrow" style={{ marginBottom: "0.6rem" }}>
            <MessageCircleQuestion size={14} /> Ask about its history
          </div>
          <form className="row" style={{ gap: "0.5rem", flexWrap: "nowrap" }} onSubmit={(e) => { e.preventDefault(); void runAsk(); }}>
            <input placeholder="Ask about this plant's care…" value={question} onChange={(e) => setQuestion(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
            <button type="submit" className="btn btn-primary btn-pill" style={{ flexShrink: 0, padding: "0 1.1rem" }} disabled={askBusy || !question.trim() || fenced || offline}>
              {askBusy ? "…" : "Ask"}
            </button>
          </form>

          {!askText && !askErr && !askBusy && !fenced && !offline && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginTop: "0.7rem" }}>
              {suggestions.map((s) => (
                <button key={s} className="chip chip-suggest" onClick={() => void runAsk(s)}>{s}</button>
              ))}
            </div>
          )}

          {/* Inline fence preview — refuse BEFORE a billed call. */}
          {fenced && !askBusy && (
            <div className="notice notice-warn fade-up" style={{ marginTop: 10 }}>
              <ShieldAlert size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
              <div>{REFUSAL_MESSAGE}</div>
            </div>
          )}

          {askBusy && (
            <div aria-live="polite" style={{ marginTop: 10, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
              <div className="skeleton-line" style={{ width: "100%" }} />
              <div className="skeleton-line" style={{ width: "75%" }} />
            </div>
          )}
          {askErr && !askBusy && <BrainErr err={askErr} onRetry={() => void runAsk()} />}
          {askText && !askBusy && (
            <div className="fade-up" style={{ marginTop: 10 }} aria-live="polite">
              <p style={{ fontSize: "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>{askText}</p>
              <div className="row" style={{ marginTop: 10, justifyContent: "flex-end" }}>
                <CopyButton text={askText} />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="row" style={{ marginTop: 12 }}>
        <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>Reads back your own log. Anything beyond it is summarised with sources, or flagged as AI reasoning — never health or safety advice.</span>
      </div>
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

// Build the plain-text copy payload: the narration plus its grounding facts (no markup).
function copyableNarration(text: string, facts: HeadlineFact[]): string {
  const lines = facts.slice(0, 5).map((f) => `• ${f.text}`).join("\n");
  return lines ? `${text}\n\nThe numbers behind this:\n${lines}` : text;
}

// The receipts: the deterministic facts the narration drew from. Purely observational.
function FactList({ facts }: { facts: HeadlineFact[] }) {
  const list = facts.slice(0, 5);
  if (list.length === 0) return null;
  const dotClass = (t: FactTone) => (t === "good" ? "good" : t === "watch" ? "watch" : "");
  return (
    <div style={{ marginTop: "0.85rem", paddingTop: "0.5rem", borderTop: "1px solid var(--border-subtle)" }}>
      <div className="stat-lbl" style={{ marginBottom: "0.1rem" }}>The numbers behind this</div>
      {list.map((f, i) => (
        <div key={i} className="fact">
          <span className={`fact-dot ${dotClass(f.tone)}`} />
          <span className="fact-text">{f.text}</span>
        </div>
      ))}
    </div>
  );
}

// Shared brain-error renderer (no-funds → "add funds in your Oriora account", NO amount).
function BrainErr({ err, onRetry }: { err: NarrErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep narrating</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  // The PLANT compliance fence — a refused question. Calm, explanatory, no retry.
  if (err.code === "refused") {
    return (
      <div className="notice notice-warn fade-up" style={{ marginTop: 12 }}>
        <ShieldAlert size={16} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      {retryable && (
        <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

// ── Add / edit a plant (bottom sheet) ───────────────────────────────────────────
function PlantForm({ editing, onClose, onSaved }: { editing: Plant | null; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [species, setSpecies] = useState(editing?.species ?? "");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [pottedOn, setPottedOn] = useState(editing?.potted_on ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  // "high"|"medium"|"low" when species was filled from a photo-ID; cleared on manual species edits.
  const [speciesConf, setSpeciesConf] = useState(editing?.species_confidence ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Photo picking (cloned from oriora_closet): a hidden <input type=file> opens the native
  // photo/camera sheet on iOS and the file picker on desktop, then the photo is downscaled on a
  // canvas and stored as a JPEG data URL in photo_ref — ON-DEVICE only. It is never uploaded to
  // Oriora; the optional species-ID sends it from this device straight to the AI vendor on the
  // customer's own key. (Unlike closet we use the input on EVERY surface so photo_ref is always
  // a renderable data URL the vision call can also consume — a path from the desktop dialog is neither.)
  const photoInputRef = useRef<HTMLInputElement>(null);
  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); return; }
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale)), h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setPhotoRef(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { setPhotoRef(canvas.toDataURL("image/jpeg", 0.82)); } catch { setPhotoRef(src); }
      };
      img.onerror = () => setPhotoRef(src);
      img.src = src;
    };
    reader.onerror = () => setErr("Could not read that photo — try another.");
    reader.readAsDataURL(file);
  }

  async function save() {
    if (!name.trim()) return;
    setSaving(true); setErr(null);
    try {
      const patch = {
        name: name.trim(), species: species.trim(), location: location.trim(), potted_on: pottedOn.trim() || null,
        photo_ref: photoRef, species_confidence: species.trim() ? speciesConf : "",
      };
      if (isEditing) await store.updatePlant(editing!.id, patch);
      else await store.createPlant(patch);
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={isEditing ? "Edit plant" : "Add a plant"} onCancel={onClose}>
      <Field label="Name">
        <input autoFocus placeholder="e.g. Fiddle-leaf fig (the big one)" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>

      {/* Photo — stored on this device only (data URL in local SQLite). */}
      <div className="row" style={{ gap: "0.6rem", alignItems: "center", margin: "0.2rem 0 0.9rem" }}>
        {photoRef ? (
          <img src={photoRef} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid var(--border-subtle)" }} />
        ) : (
          <div style={{ width: 56, height: 56, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "var(--elev-2)" }} aria-hidden><Camera size={20} /></div>
        )}
        <button type="button" className="btn btn-sm" onClick={() => photoInputRef.current?.click()} style={{ whiteSpace: "nowrap" }}>{photoRef ? "Change photo" : "Add photo"}</button>
        {photoRef && <button type="button" className="btn btn-sm" onClick={() => setPhotoRef("")} style={{ whiteSpace: "nowrap" }}>Remove</button>}
        <input ref={photoInputRef} type="file" accept="image/*" onChange={onPhotoInputChange} style={{ display: "none" }} />
      </div>

      <Field label="Species" optional>
        <input placeholder="e.g. Ficus lyrata" value={species} onChange={(e) => { setSpecies(e.target.value); setSpeciesConf(""); }} />
        {species.trim() !== "" && speciesConf !== "" && (
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 4 }}>Identified from the photo · {speciesConf} confidence</div>
        )}
      </Field>

      {/* Photo → species-ID (device-direct, customer's own key; native surfaces only). */}
      {platform() !== "web" && photoRef !== "" && (
        <SpeciesIdBlock photoRef={photoRef} onUse={(r) => { setSpecies(r.species); setSpeciesConf(r.confidence); }} />
      )}
      <Field label="Location" optional>
        <input placeholder="e.g. Living-room window" value={location} onChange={(e) => setLocation(e.target.value)} />
      </Field>
      <Field label="Last potted" optional>
        <input type="date" max={todayLocal()} value={pottedOn} onChange={(e) => setPottedOn(e.target.value)} />
      </Field>

      {err && <div className="notice notice-err" style={{ marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={!name.trim() || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Add plant"}</button>
      </div>
    </BottomSheet>
  );
}

// ── Photo → species-ID (through the Oriora brain; the ROUTER picks the model) ─────
// §5d 2026-07-27: this used to be a device-direct call to a named vendor's vision endpoint,
// gated on the customer's own vendor key kept in an on-device file. Removed — no app names or
// calls an AI vendor, and no key is handled inside an app. There is no key gate any more.
// ONE billed brain call per tap; the result only fills the Species field — nothing is saved
// until the user presses Add plant / Update (user confirms before save).
function SpeciesIdBlock({ photoRef, onUse }: { photoRef: string; onUse: (r: SpeciesId) => void }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SpeciesId | null>(null);
  const [used, setUsed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // §5d 2026-07-27: no key gate. Identification is an ordinary billed brain call — the ROUTER
  // picks an image-capable model. This used to require the customer's own vendor key to be
  // present on the device before the button would even appear.
  async function run() {
    setBusy(true); setErr(null); setResult(null); setUsed(false);
    try {
      setResult(await identifySpecies(photoRef));
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg || "Identification failed — please try again.");
    } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ padding: "0.85rem 1rem", marginBottom: "0.9rem", borderRadius: "var(--radius-lg)" }}>
      <div className="eyebrow" style={{ marginBottom: "0.4rem" }}><Sparkles size={13} /> Identify species from the photo</div>

      <>
          {!result && (
            <div className="row" style={{ gap: "0.5rem" }}>
              <button type="button" className="btn btn-primary btn-sm btn-pill" disabled={busy} onClick={() => void run()}>
                {busy ? "Identifying…" : (<><Camera size={13} /> Identify species</>)}
              </button>
            </div>
          )}
          {busy && (
            <div aria-live="polite" style={{ marginTop: 8, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 7 }}>
              <div className="skeleton-line" style={{ width: "82%" }} />
              <div className="skeleton-line" style={{ width: "55%" }} />
            </div>
          )}
          {result && !busy && (
            <div className="fade-up" aria-live="polite">
              <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>{result.species}</div>
              <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 2 }}>{result.confidence} confidence · your confirmation is what saves it</div>
              <div className="row" style={{ gap: "0.4rem", marginTop: 8 }}>
                {!used ? (
                  <button type="button" className="btn btn-primary btn-sm btn-pill" onClick={() => { onUse(result); setUsed(true); }}>Use as species</button>
                ) : (
                  <span className="chip ok" style={{ fontSize: "var(--fs-xxs)" }}><Check size={11} /> Filled — save the plant to keep it</span>
                )}
                <button type="button" className="btn btn-sm" onClick={() => { setResult(null); setUsed(false); }}>Dismiss</button>
              </div>
            </div>
          )}
          <p className="muted" style={{ fontSize: "var(--fs-xxs)", lineHeight: 1.5, margin: "0.6rem 0 0" }}>
            The photo passes through Oriora, which doesn't retain it. Factual species lookup only; no plant-health advice.
          </p>
      </>

      {err && !busy && <div className="notice notice-err" style={{ marginTop: 8, fontSize: "var(--fs-xs)" }}>{err}</div>}
    </div>
  );
}

// ── Log a care event (bottom sheet) ─────────────────────────────────────────────
function LogCare({ plant, onClose, onSaved }: { plant: Plant; onClose: () => void; onSaved: () => void }) {
  const [kind, setKind] = useState<CareKind>("water");
  const [doneOn, setDoneOn] = useState(todayLocal());
  const [note, setNote] = useState("");
  // A photo ON the moment is what turns the care log into an album — the same picker the
  // plant's own photo uses, downscaled on-device, never uploaded.
  const [photoRef, setPhotoRef] = useState("");
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onPhotoPicked(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setErr(null);
    try { setPhotoRef(await readDownscaledPhoto(file)); }
    catch (ex) { setErr(ex instanceof Error ? ex.message : "Could not read that photo — try another."); }
  }

  async function save() {
    setSaving(true); setErr(null);
    try {
      await store.createEvent({ plant_id: plant.id, kind, done_on: doneOn, note: note.trim(), photo_ref: photoRef });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={`Log care · ${plant.name}`} onCancel={onClose}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.6rem" }}>What did you do?</div>
      <div className="row" style={{ gap: "0.4rem", marginBottom: "1.1rem", flexWrap: "wrap" }}>
        {CARE_KINDS.map((k) => (
          <button key={k.kind} type="button" className={`kind-rung${kind === k.kind ? " selected" : ""}`} onClick={() => setKind(k.kind)} aria-pressed={kind === k.kind} aria-label={k.verb}>
            <span className="emoji">{k.emoji}</span>
            <span className="lbl">{k.verb}</span>
          </button>
        ))}
      </div>

      <Field label="When">
        <input type="date" max={todayLocal()} value={doneOn} onChange={(e) => setDoneOn(e.target.value)} />
      </Field>
      <Field label={kind === "milestone" ? "What happened?" : "Note"} optional>
        <textarea rows={3}
          placeholder={kind === "milestone" ? "e.g. first new leaf unfurled · flowered · took a cutting" : kind === "photo" ? "optional — what does it look like today?" : "e.g. soil was bone dry, new leaf unfurling"}
          value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {/* Photo on the moment — builds the plant's album over time. */}
      <input ref={photoInputRef} type="file" accept="image/*" onChange={(e) => void onPhotoPicked(e)} style={{ display: "none" }} />
      <Field label="Photo" optional>
        {photoRef ? (
          <div className="row" style={{ gap: "0.6rem", alignItems: "center" }}>
            <img src={photoRef} alt="" style={{ width: 64, height: 64, objectFit: "cover", borderRadius: "var(--radius-md)", flexShrink: 0 }} />
            <button type="button" className="btn btn-sm btn-pill" onClick={() => photoInputRef.current?.click()}>Replace</button>
            <button type="button" className="link" style={{ fontSize: "var(--fs-xxs)" }} onClick={() => setPhotoRef("")}>Remove</button>
          </div>
        ) : (
          <button type="button" className="btn btn-sm btn-pill" onClick={() => photoInputRef.current?.click()}>
            <Camera size={13} /> Add a photo
          </button>
        )}
      </Field>

      {err && <div className="notice notice-err" style={{ marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
      </div>
    </BottomSheet>
  );
}

// ── Add a growth measurement ───────────────────────────────────────────────────
function LogGrowth({ plant, onClose, onSaved }: { plant: Plant; onClose: () => void; onSaved: () => void }) {
  const [measuredOn, setMeasuredOn] = useState(todayLocal());
  const [height, setHeight] = useState("");
  const [leaves, setLeaves] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const h = height.trim() === "" ? null : Number(height);
  const l = leaves.trim() === "" ? null : Number(leaves);
  // Mirrors the Rust guard: at least one number, and whatever is given must be a real number.
  const valid = (h !== null && Number.isFinite(h)) || (l !== null && Number.isFinite(l));

  async function save() {
    if (!valid) return;
    setSaving(true); setErr(null);
    try {
      await store.createGrowth({
        plant_id: plant.id, measured_on: measuredOn, note: note.trim(),
        height_cm: h !== null && Number.isFinite(h) ? h : null,
        leaf_count: l !== null && Number.isFinite(l) ? Math.round(l) : null,
      });
      onSaved();
    } catch (e) {
      setErr(`Could not save — try again. (${e instanceof Error ? e.message : String(e)})`);
      setSaving(false);
    }
  }

  return (
    <BottomSheet title={`Measure · ${plant.name}`} onCancel={onClose}>
      <Field label="When">
        <input type="date" max={todayLocal()} value={measuredOn} onChange={(e) => setMeasuredOn(e.target.value)} />
      </Field>
      <Field label="Height (cm)" optional>
        <input type="number" inputMode="decimal" min="0" step="0.5" placeholder="e.g. 42" value={height} onChange={(e) => setHeight(e.target.value)} />
      </Field>
      <Field label="Leaves" optional>
        <input type="number" inputMode="numeric" min="0" step="1" placeholder="e.g. 11" value={leaves} onChange={(e) => setLeaves(e.target.value)} />
      </Field>
      <Field label="Note" optional>
        <input placeholder="e.g. measured to the tallest leaf" value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>

      {err && <div className="notice notice-err" style={{ marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving || !valid}>{saving ? "Saving…" : "Save"}</button>
      </div>
      {!valid && <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.5rem", textAlign: "right" }}>Add a height or a leaf count.</div>}
    </BottomSheet>
  );
}

function ConfirmDeletePlant({ plant, onCancel, onConfirm }: { plant: Plant; onCancel: () => void; onConfirm: () => void }) {
  return (
    <CenterSheet title="Delete this plant?" onCancel={onCancel}>
      <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.1rem", lineHeight: 1.55 }}>
        Delete “{plant.name}” and its entire care timeline? This can’t be undone.
      </p>
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onCancel}>Cancel</button>
        <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
      </div>
    </CenterSheet>
  );
}

// ── Sheet primitives ─────────────────────────────────────────────────────────
function Field({ label, optional, children }: { label: string; optional?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: "0.9rem" }}>
      <label style={{ fontWeight: 600, fontSize: "var(--fs-sm)", display: "block", marginBottom: "0.4rem" }}>
        {label} {optional && <span className="muted" style={{ fontWeight: 400 }}>(optional)</span>}
      </label>
      {children}
    </div>
  );
}

// Bottom sheet — the input-heavy modal (Add plant / Log care). Mobile-native feel.
function BottomSheet({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-bottom" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, margin: "0 auto" }}>
        <div className="sheet-grab" />
        <div className="row" style={{ justifyContent: "space-between", marginBottom: "1rem" }}>
          <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem" }}>{title}</div>
          <button className="tile-del" aria-label="Close" onClick={onCancel}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Centered sheet — for short confirmations.
function CenterSheet({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.15rem", marginBottom: "0.9rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
